import { useCallback, useMemo, ReactNode, lazy, Suspense } from 'react';

import { AnimatePresence } from 'framer-motion';
import { Switch } from 'react-router-dom';

import { LoadingOverlay } from 'components';
import { useBootstrapApp, useAuth } from 'hooks';
import { RouteConfigItem } from 'models';
import { generateRoutes, getNormalizedRoutes } from 'utils';

import { MotionRedirect, AnimationType, Route, PrivatePageWrapper } from '.';

const NoData = lazy<(props: any) => JSX.Element>(() => import('pages/NoData'));
const BootstrapPage = lazy(() => import('pages/Bootstrap'));

type CustomContextProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  children: ReactNode;
};
type CustomContextType = (props: CustomContextProps) => JSX.Element;

interface PrivateRoutesProps {
  routesConfig?: RouteConfigItem[];
  redirectPath?: string;
  CustomContext?: CustomContextType;
  animationType?: AnimationType;
}

export function PrivateRoutes({
  routesConfig,
  redirectPath,
  CustomContext,
  animationType = 'leftToRight',
}: PrivateRoutesProps): JSX.Element {
  const { contexts, globalLoading } = useBootstrapApp();
  const { user, token } = useAuth();

  const memoCanAccessApplication = useMemo(() => {
    return !!(user && token);
  }, [user, token]);

  const memoRoutes = useMemo(() => {
    const contextRoutes = generateRoutes(contexts);
    const decoratedRoutes = getNormalizedRoutes(routesConfig ?? contextRoutes);

    return decoratedRoutes;
  }, [contexts, routesConfig]);

  const memoRedirectPath = useMemo(() => {
    if ((!globalLoading && !contexts.length) || !memoCanAccessApplication) {
      return '/no-data';
    }

    const [route] = memoRoutes;

    if (route) {
      return route.path;
    }

    const { search } = window.location;

    return redirectPath ?? `/${search}`;
  }, [
    globalLoading,
    contexts.length,
    memoRoutes,
    redirectPath,
    memoCanAccessApplication,
  ]);

  const importComponent = useCallback((componentPath: string) => {
    return lazy(() => import(`pages/${componentPath}` as const));
  }, []);

  const renderRoute = useCallback(
    (route: RouteConfigItem) => {
      const {
        componentPath,
        component,
        props: routeProps,
        suspenseComponent,
      } = route;

      const Component = componentPath
        ? importComponent(componentPath)
        : component;
      const props = routeProps || {};

      const SuspenseComponent = suspenseComponent ?? LoadingOverlay;

      return () => {
        if (route.context) {
          return (
            <PrivatePageWrapper noPadding={route.wrapperNoPadding}>
              <Suspense fallback={<SuspenseComponent />}>
                <route.context>
                  {Component && <Component {...props} />}
                </route.context>
              </Suspense>
            </PrivatePageWrapper>
          );
        }

        return (
          <PrivatePageWrapper noPadding={route.wrapperNoPadding}>
            <Suspense fallback={<SuspenseComponent />}>
              {Component && <Component {...props} />}
            </Suspense>
          </PrivatePageWrapper>
        );
      };
    },
    [importComponent],
  );

  const CustomContextProvider = useCallback(
    ({ children }: CustomContextProps) => {
      if (!CustomContext) {
        return <>{children}</>;
      }

      return <CustomContext>{children}</CustomContext>;
    },
    [CustomContext],
  );

  return (
    <CustomContextProvider>
      <AnimatePresence exitBeforeEnter>
        <Switch>
          <Route
            path="/"
            exact
            isPrivate
            component={() => (
              <Suspense fallback={<LoadingOverlay />}>
                <BootstrapPage />
              </Suspense>
            )}
          />
          {!globalLoading && (
            <Route
              path="/no-data"
              exact
              component={() => (
                <Suspense fallback={<LoadingOverlay />}>
                  <NoData showSubtitle={false} />
                </Suspense>
              )}
            />
          )}
          {memoRoutes.map(route => {
            return (
              <Route
                key={route.id}
                exact={route.exact}
                path={route.path}
                isPrivate={route.isPrivate}
                redirectPath={redirectPath}
                component={renderRoute(route)}
                animationType={animationType}
                roles={route.roles}
                hasMotionWrapper
              />
            );
          })}
          <MotionRedirect to={memoRedirectPath} />
        </Switch>
      </AnimatePresence>
    </CustomContextProvider>
  );
}
